import { useMemo } from "react";
import { CalendarDays } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shift = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

export const PRESETS = {
  today: { label: "Today", range: () => [iso(new Date()), iso(new Date())] },
  yesterday: { label: "Yesterday", range: () => [iso(shift(-1)), iso(shift(-1))] },
  last7: { label: "Last 7 days", range: () => [iso(shift(-6)), iso(new Date())] },
  last30: { label: "Last 30 days", range: () => [iso(shift(-29)), iso(new Date())] },
  this_month: {
    label: "This month",
    range: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(n)];
    },
  },
  last_month: {
    label: "Last month",
    range: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
              iso(new Date(n.getFullYear(), n.getMonth(), 0))];
    },
  },
  this_year: {
    label: "This year",
    range: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), 0, 1)), iso(n)];
    },
  },
  custom: { label: "Custom range", range: null },
};

export const formatPeriod = (from, to) => {
  const fmt = (v) => {
    if (!v) return "";
    const d = new Date(`${v}T00:00:00`);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  return from === to ? fmt(from) : `${fmt(from)} - ${fmt(to)}`;
};

/** Reporting period picker. The chosen period lives in the URL so it survives tab switches and refreshes. */
export const DateRangePicker = ({ preset, from, to, onChange }) => {
  const label = useMemo(() => formatPeriod(from, to), [from, to]);

  const choosePreset = (key) => {
    if (key === "custom") {
      onChange({ preset: "custom", from, to });
      return;
    }
    const [f, t] = PRESETS[key].range();
    onChange({ preset: key, from: f, to: t });
  };

  return (
    <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 mb-6" data-testid="report-date-range">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-[#205B5A]">
          <CalendarDays className="w-4 h-4" />
          <span className="text-sm font-medium text-[#1A1B1A]" data-testid="report-period-label">{label}</span>
        </div>
        <select value={preset} onChange={(e) => choosePreset(e.target.value)}
          data-testid="report-preset-select"
          className="ml-auto text-xs bg-[#FAF9F6] border border-[#1A1B1A]/10 rounded-xl px-3 py-2 text-[#1A1B1A]">
          {Object.entries(PRESETS).map(([k, p]) => (
            <option key={k} value={k}>{p.label}</option>
          ))}
        </select>
        {preset === "custom" && (
          <div className="flex items-center gap-2" data-testid="report-custom-range">
            <label className="text-[10px] text-[#757675]">From</label>
            <input type="date" value={from} data-testid="report-date-from"
              onChange={(e) => onChange({ preset: "custom", from: e.target.value, to })}
              className="text-xs bg-[#FAF9F6] border border-[#1A1B1A]/10 rounded-xl px-2.5 py-2" />
            <label className="text-[10px] text-[#757675]">To</label>
            <input type="date" value={to} data-testid="report-date-to"
              onChange={(e) => onChange({ preset: "custom", from, to: e.target.value })}
              className="text-xs bg-[#FAF9F6] border border-[#1A1B1A]/10 rounded-xl px-2.5 py-2" />
          </div>
        )}
      </div>
    </div>
  );
};
